import type { Accent } from "../api/types";

/** Coloured square with the agent's initials, as in the mockups. */
export function IconTile({ initials, accent }: { initials: string; accent: Accent }) {
  const c = `var(--a-${accent})`;
  return (
    <span
      className="icon-tile"
      style={{ background: `color-mix(in srgb, ${c} 14%, transparent)`, color: c }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

export function StatusPill({ status }: { status: "active" | "idle" | "planned" }) {
  if (status === "active") {
    return (
      <span className="pill pill-good">
        <span className="dot dot-active" aria-hidden="true" />
        Active
      </span>
    );
  }
  if (status === "idle") {
    return (
      <span className="pill pill-warning" title="Registered and dispatchable, but it has not run yet.">
        <span className="dot dot-idle" aria-hidden="true" />
        Idle
      </span>
    );
  }
  return (
    <span className="pill" title="On the roadmap. No backing service exists and nothing dispatches to it.">
      <span className="dot dot-planned" aria-hidden="true" />
      Planned
    </span>
  );
}

/** Log status pill: colour plus the word itself, never colour alone. */
export function LogStatus({ status }: { status: string }) {
  const cls =
    status === "Success" ? "pill-good"
    : status === "Escalated" ? "pill-warning"
    : status === "Rejected" || status === "Failed" ? "pill-critical"
    : "pill-info";
  return <span className={`pill ${cls}`}>{status}</span>;
}

/**
 * Guardrail state switch. Rendered as a switch because that is the design, but
 * it is deliberately inert and marked so to assistive tech -- nothing in this
 * app writes configuration, and a control that looked operable while changing
 * nothing would be worse than showing the state plainly.
 */
export function StateSwitch({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className="switch"
      data-on={on}
      role="img"
      aria-label={`${label}: ${on ? "on" : "off"} (read-only)`}
      title="Read-only. Change this where the value lives - see the source path below."
    />
  );
}
